import { Funnel, FunnelTypes, NodeVariants } from 'features/co-pilot/co-pilot.types';
import { Offer } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import { TFunction } from 'i18next';
import { useCallback } from 'react';
import { Edge, Node } from 'reactflow';

const getStartingNodes = (nodes: Node[], edges: Edge[]) => {
  const targetNodes = edges.map((edge) => edge.target);
  return nodes.filter((node) => !targetNodes.includes(node.id));
};

const useCampaignStartValidation = (campaign: Funnel | null, t: TFunction) => {
  const isStartCampaignPossible = useCallback(
    (fetchedCampaign: Funnel, linkedinAccounts: LinkedinAccount[]) => {
      const { reactFlow, offer: fetchedOffer, linkedinAccount } = fetchedCampaign;

      // Get work time from the LinkedIn account, not the funnel
      const accountData = linkedinAccounts.find((acc) => acc._id === linkedinAccount);
      const workDays = accountData?.workDays || [];
      const workHours = accountData?.workHours || [];

      const workDaysSet = !_.isEmpty(workDays);
      const workHoursSet = !_.isEmpty(workHours);

      const functionNodes = reactFlow?.nodes.filter((node) => node.type === NodeVariants.FUNCTION_NODE) || [];
      const allNodesConnected = getStartingNodes(reactFlow?.nodes || [], reactFlow?.edges || []).length <= 1;

      const functionsSet = !_.isNil(reactFlow) && functionNodes.length > 0;
      const offerSet = (() => {
        if (_.isNil(fetchedOffer)) return false;

        const isFieldFilled = (field: any): boolean => {
          if (_.isNil(field)) return false;
          if (typeof field === 'string') return field.trim() !== '';
          if (typeof field === 'object') {
            if ('inUse' in field) {
              if (!(field as any).inUse) return true;
              const val = 'value' in field ? ((field as any).value as string) : '';
              return val.trim() !== '';
            }
            if ('value' in field) {
              const val = (field as any).value as string;
              return val.trim() !== '';
            }
          }
          return false;
        };

        const requiredFields: (keyof Offer)[] =
          fetchedCampaign.type === FunnelTypes.ACTIVE_SOURCING_CO_PILOT
            ? ['jobOffer']
            : fetchedCampaign.type === FunnelTypes.SOCIAL_SELLING_CO_PILOT
            ? ['problems', 'features', 'benefits', 'impact']
            : [];

        const allRequiredFilled = requiredFields.every((k) => isFieldFilled((fetchedOffer as any)[k]));

        if (requiredFields.length === 0) {
          const { customPlaceholders, ...rest } = fetchedOffer as any;
          const baseFieldsFilled = _.every(Object.values(rest), (o) => isFieldFilled(o));
          const placeholdersFilled = !_.isEmpty(customPlaceholders);
          return baseFieldsFilled || placeholdersFilled;
        }

        const placeholdersFilled = !_.isEmpty((fetchedOffer as any).customPlaceholders);
        return allRequiredFilled || placeholdersFilled;
      })();

      const linkedinAccountSelected = !_.isNil(linkedinAccount);

      const issues = [];

      if (!workDaysSet) issues.push(t('workDaysNotSet'));
      if (!workHoursSet) issues.push(t('workHoursNotSet'));
      if (!functionsSet) issues.push(t('notAllFunctionsSet'));
      if (!allNodesConnected) issues.push(t('allNodesNotConnected'));
      if (!offerSet) issues.push(t('offerNotSet'));
      if (!linkedinAccountSelected) issues.push(t('linkedinAccountNotSelected'));

      const isPossible =
        !_.isNil(campaign) &&
        workDaysSet &&
        workHoursSet &&
        functionsSet &&
        allNodesConnected &&
        offerSet &&
        linkedinAccountSelected;

      return { isPossible, issues };
    },
    [campaign, t]
  );

  return { isStartCampaignPossible };
};

export default useCampaignStartValidation;
